import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const UpdateItem = ({ u, checked, toggle }) => (
  <label className="flex items-start gap-3 py-2.5 border-b last:border-0 border-slate-100 cursor-pointer" data-testid={`bulk-update-${u.key}`}>
    <Checkbox checked={checked} onCheckedChange={toggle} className="mt-0.5" data-testid={`bulk-update-check-${u.key}`} />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium">{u.title}{u.version ? ` → ${u.version}` : ""}</p>
      <p className="text-xs text-slate-500">
        {u.kind === "os" ? "Système" : "Logiciel"} · {u.machines.length} poste(s) : {u.machines.map((m) => m.name + (m.online ? "" : " (hors ligne)")).join(", ")}
        {u.reboot && " · redémarrage requis"}
      </p>
    </div>
    {u.critical && <span className="text-[10px] font-semibold uppercase rounded bg-red-50 text-red-700 px-1.5 py-0.5">Critique</span>}
  </label>
);

export const BulkUpdateDialog = ({ ids, open, onClose, onDone }) => {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setList(null); setSel([]);
    api.post("/rmm/bulk/updates", { machine_ids: ids }).then(({ data }) => setList(data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [open, ids, onClose]);
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const install = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rmm/bulk/install", { machine_ids: ids, keys: sel });
      if (data.started.length) toast.success(`Installation lancée sur ${data.started.length} poste(s). Les utilisateurs seront invités à redémarrer si nécessaire.`);
      data.skipped.forEach((s) => toast.warning(`${s.name} ignoré : ${s.reason}`));
      if (!data.started.length && !data.skipped.length) toast.info("Aucun poste concerné par cette sélection");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-update-dialog">
        <DialogHeader>
          <DialogTitle>Mettre à jour {ids.length} poste(s)</DialogTitle>
          <DialogDescription>Cochez les mises à jour à installer : chacune sera installée sur tous les postes sélectionnés qui en ont besoin.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto">
          {!list && <Loader2 className="h-5 w-5 animate-spin mx-auto my-8 text-slate-400" />}
          {list && !list.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="bulk-update-empty">Aucune mise à jour connue. Lancez d'abord « Rechercher les mises à jour ».</p>}
          {list?.length > 0 && (
            <button type="button" className="text-xs text-blue-600 mb-1" onClick={() => setSel(sel.length === list.length ? [] : list.map((u) => u.key))} data-testid="bulk-select-all">
              {sel.length === list.length ? "Tout désélectionner" : "Tout sélectionner"}
            </button>
          )}
          {list?.map((u) => <UpdateItem key={u.key} u={u} checked={sel.includes(u.key)} toggle={() => toggle(u.key)} />)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-cancel-btn">Annuler</Button>
          <Button onClick={install} disabled={!sel.length || busy} data-testid="bulk-install-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Installer ({sel.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
